import React, { useState } from 'react';
import DataTable from 'react-data-table-component';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchCompanies,
  deleteCompany,
  setCompanyEdit,
  clearCompanyEdit,
} from '../../../redux/actions/masters/company/companyAction';
import { hasPermission } from '../../../utills/hasPermission';
import { showConfirm, showInfo } from '../../../utills/sweetAlert';

const cardStyle = {
  background: 'transparent',
  border: 'none',
  boxShadow: 'none',
  padding: 0,
  minWidth: 0,
  marginBottom: 0,
  fontFamily: 'Poppins, sans-serif',
};

const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 18,
};

const titleStyle = {
  fontWeight: 700,
  fontSize: 18,
  color: '#111',
  letterSpacing: 0.7,
};

const newBtnStyle = {
  background: 'linear-gradient(135deg, #00C853 0%, #43A047 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '10px 26px',
  fontWeight: 700,
  fontSize: 16,
  cursor: 'pointer',
  marginRight: 12,
  letterSpacing: 0.5,
  transition: 'all 0.3s ease',
  boxShadow: '0 2px 8px rgba(0, 200, 83, 0.3)',
};

const searchContainer = {
  position: 'relative',
  minWidth: 200,
  marginLeft: 8,
};

const searchInput = {
  width: 200,
  padding: '10px 36px 10px 38px',
  border: '1.5px solid #C8E6C9',
  borderRadius: 8,
  fontSize: 16,
  color: '#1B5E20',
  background: '#fff',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border 0.3s',
};

const searchIconStyle = {
  position: 'absolute',
  left: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  color: '#00C853', // Ensure green color
  fontSize: 20,
  pointerEvents: 'none',
};

const actionStyle = {
  cursor: 'pointer',
  marginRight: 10,
  background: 'none',
  border: 'none',
  borderRadius: 6,
  padding: 4,
  color: '#757575',
  transition: 'all 0.3s ease',
};

const columnHeaderStyle = {
  fontFamily: 'Poppins, sans-serif',
  fontSize: 15,
  fontWeight: 600,
  color: '#111',
};
const cellStyle = {
  fontFamily: 'Poppins, sans-serif',
  fontSize: 15,
  color: '#222',
};

const CompanyTable = () => {
  const dispatch = useDispatch();
  const { list = [], loading, error } = useSelector((state) => state.company);
  const [search, setSearch] = useState('');
  const [hoveredAction, setHoveredAction] = useState({});

  React.useEffect(() => {
    dispatch(fetchCompanies());
  }, [dispatch]);

  const handleEdit = async (company) => {
    await showInfo('Edit Company', `Editing company: ${company.company_name}`);
    dispatch(setCompanyEdit(company));
  };

  const handleDelete = async (id, name) => {
    const confirmed = await showConfirm('Trash Company', `Are you sure you want to trash company: ${name}?`, 'Trash', 'Cancel');
    if (confirmed) {
      dispatch(deleteCompany(id));
    }
  };

  const handleNew = () => {
    dispatch(clearCompanyEdit());
  };

  const filteredCompanies = list.filter(c =>
    c.company_name && c.company_name.toLowerCase().includes(search.toLowerCase()) && c.trash === 0
  );

  const columns = [
    {
      name: 'Company Code',
      selector: row => row.company_code,
      sortable: true,
      style: cellStyle,
      headerStyle: columnHeaderStyle,
      width: '150px',
      
    },
    {
      name: 'Company Name',
      selector: row => row.company_name,
      sortable: true,
      style: cellStyle,
      headerStyle: columnHeaderStyle,
      width: '150px',

    },
    {
      name: 'Email',
      selector: row => row.email,
      sortable: true,
      style: cellStyle,
      headerStyle: columnHeaderStyle,
    },
    {
      name: 'Phone Number',
      selector: row => row.phone_number,
      sortable: true,
      style: cellStyle,
      headerStyle: columnHeaderStyle,
      width: '150px',

    },
    {
      name: 'GST No',
      selector: row => row.gst_no,
      sortable: true,
      style: cellStyle,
      headerStyle: columnHeaderStyle,
    },
    {
      name: 'License No',
      selector: row => row.license_no,
      sortable: true,
      style: cellStyle,
      headerStyle: columnHeaderStyle,
    },
    {
      name: 'Actions',
      cell: row => (
        <>
          <button
            style={actionStyle}
            onClick={() => handleEdit(row)}
            title="Edit"
            onMouseEnter={() => setHoveredAction({ id: row.company_id, type: 'edit' })}
            onMouseLeave={() => setHoveredAction({})}
          >
            <EditIcon fontSize="small" />
            {hoveredAction.id === row.company_id && hoveredAction.type === 'edit' && (
              <span style={{ marginLeft: 4, color: '#1B5E20', fontWeight: 600 }}>Edit</span>
            )}
          </button>
          <button
            style={actionStyle}
            onClick={() => handleDelete(row.company_id, row.company_name)}
            title="Trash"
            onMouseEnter={() => setHoveredAction({ id: row.company_id, type: 'delete' })}
            onMouseLeave={() => setHoveredAction({})}
          >
            <DeleteOutlineIcon fontSize="small" />
            {hoveredAction.id === row.company_id && hoveredAction.type === 'delete' && (
              <span style={{ marginLeft: 4, color: '#d32f2f', fontWeight: 600 }}>Trash</span>
            )}
          </button>
        </>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      style: cellStyle,
      headerStyle: columnHeaderStyle,
    },
  ];

  return (
    <div style={cardStyle}>
      <style>{`
        /* Custom horizontal scrollbar for the table */
        .company-table ::-webkit-scrollbar {
          height: 6px;
          background: #f2f2f2;
        }
        .company-table ::-webkit-scrollbar-thumb {
          background: #d3d3d3;
          border-radius: 4px;
        }
        .company-table {
          scrollbar-color: #d3d3d3 #f2f2f2;
          scrollbar-width: thin;
        }
      `}</style>
      <div style={headerStyle}>
        <span style={titleStyle}>Companies</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button style={newBtnStyle} onClick={handleNew}>+ New</button>
          <div style={searchContainer}>
            <input
              style={searchInput}
              type="text"
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <span style={searchIconStyle}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00C853" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg></span>
          </div>
        </div>
      </div>
      <div className="company-table">
        <DataTable
          columns={columns}
          data={filteredCompanies}
          pagination
          highlightOnHover
          striped
          fixedHeader
          fixedHeaderScrollHeight="420px"
          progressPending={loading}
          noDataComponent={<div style={{ color: '#888', fontSize: 16 }}>No Companies found.</div>}
          customStyles={{
            headRow: { style: { background: '#E8F5E9', fontWeight: 700 } },
            rows: { style: { minHeight: 48 } },
          }}
        />
      </div>
      {error && <div style={{ color: 'red', fontSize: 15, marginTop: 8 }}>{error}</div>}
    </div>
  );
};

export default CompanyTable; 
 